import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import Layout, { ProtectedRoute } from "@/components/Layout";

import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ForcedChangePasswordPage from "@/pages/ForcedChangePasswordPage";
import MyProfilePage from "@/pages/MyProfilePage";
import HomesPage from "@/pages/HomesPage";
import KpiWeightsPage from "@/pages/KpiWeightsPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import ScoreEntryPage from "@/pages/ScoreEntryPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import StaffDirectoryPage from "@/pages/StaffDirectoryPage";
import UserManagementPage from "@/pages/UserManagementPage";
import PermissionMatrixPage from "@/pages/PermissionMatrixPage";
import QAAuditEntryPage from "@/pages/QAAuditEntryPage";
import QADashboardPage from "@/pages/QADashboardPage";
import QAReportPage from "@/pages/QAReportPage";
import AuditLogPage from "@/pages/AuditLogPage";
import ReportsPage from "@/pages/ReportsPage";
import PrintableReportPage from "@/pages/PrintableReportPage";
import { StaffMyDashboard, StaffMyHistory, StaffMyProfile } from "@/pages/StaffPortal";
import AuthRepairPage from "@/pages/AuthRepairPage";
import NoteAssistantPage from "@/pages/NoteAssistantPage";
import PolicyLibraryPage from "@/pages/PolicyLibraryPage";
import PolicyDetailPage from "@/pages/PolicyDetailPage";
import DirectoryImportPage from "@/pages/DirectoryImportPage";
import AIAssistantWidget from "@/components/AIAssistantWidget";

const ADMIN = ["CEO", "Project Manager", "Administration"];
const ALL_INTERNAL = ["CEO", "Project Manager", "Administration", "House Manager"];

function RootRedirect() {
  const { user, loading } = useAuth();
  const showLanding = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("landing") === "1";
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm animate-pulse">Loading…</div>;
  // Unauthenticated visitors always see the public landing page.
  // Authenticated users normally route to their workspace, but the in-app
  // "Back to Home" button navigates to /?landing=1 to opt out of the redirect.
  if (!user) return <LandingPage />;
  if (showLanding) return <LandingPage />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return <Navigate to={user.access_role === "Staff" ? "/me" : "/dashboard"} replace />;
}

/** Wrapper that intercepts users with must_change_password=true and forces them
 *  to the change-password page before any in-app navigation succeeds. */
function ForceChangeGate({ children }) {
  const { user } = useAuth();
  if (user?.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}

function GlobalAIWidget() {
  const { user } = useAuth();
  const location = useLocation();
  // Hide on auth screens and force-change to keep them clean
  const hideOn = ["/login", "/change-password", "/forgot-password", "/reset-password", "/auth/callback"];
  if (!user) return null;
  if (hideOn.some((p) => location.pathname.startsWith(p))) return null;
  return <AIAssistantWidget />;
}

function AppRoutes() {
  const location = useLocation();
  // Synchronously detect Emergent OAuth callback
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ProtectedRoute><ForcedChangePasswordPage /></ProtectedRoute>} />

      <Route path="/dashboard" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><DashboardPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/score-entry" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><ScoreEntryPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><ForceChangeGate><Layout><LeaderboardPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><StaffDirectoryPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><UserManagementPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/users/import" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><DirectoryImportPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/homes" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><HomesPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ForceChangeGate><Layout><MyProfilePage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/permissions" element={<ProtectedRoute allow={["CEO", "Project Manager"]}><ForceChangeGate><Layout><PermissionMatrixPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/kpi-weights" element={<ProtectedRoute allow={["CEO", "Project Manager"]}><ForceChangeGate><Layout><KpiWeightsPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/qa/entry" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><QAAuditEntryPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/qa/dashboard" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><QADashboardPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/qa/report" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><QAReportPage /></ForceChangeGate></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><Layout><ReportsPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/reports/print" element={<ProtectedRoute allow={ALL_INTERNAL}><ForceChangeGate><PrintableReportPage /></ForceChangeGate></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute allow={ADMIN}><ForceChangeGate><Layout><AuditLogPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/auth-repair" element={<ProtectedRoute allow={["CEO", "Project Manager"]}><ForceChangeGate><Layout><AuthRepairPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/ai/notes" element={<ProtectedRoute><ForceChangeGate><Layout><NoteAssistantPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/note-assistant" element={<Navigate to="/ai/notes" replace />} />
      <Route path="/policies" element={<ProtectedRoute><ForceChangeGate><Layout><PolicyLibraryPage /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/policies/:id" element={<ProtectedRoute><ForceChangeGate><Layout><PolicyDetailPage /></Layout></ForceChangeGate></ProtectedRoute>} />

      <Route path="/me" element={<ProtectedRoute><ForceChangeGate><Layout><StaffMyDashboard /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/me/history" element={<ProtectedRoute><ForceChangeGate><Layout><StaffMyHistory /></Layout></ForceChangeGate></ProtectedRoute>} />
      <Route path="/me/profile" element={<ProtectedRoute><ForceChangeGate><Layout><StaffMyProfile /></Layout></ForceChangeGate></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <GlobalAIWidget />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
